import { useState, useEffect, useRef, useCallback } from 'react';
import { Trophy } from 'lucide-react';
import apiService from '@/shared/services/api';
import { cacheService } from '@/shared/services/cacheService';
import { CACHE_KEYS, CACHE_CONFIG } from '@/shared/config/cache';
import type { ExerciseRankingsData, RankingFewestEntry, RankingExecutionTimeEntry } from '@/code/types';

interface Props {
  guideNumber: number;
  exerciseNumber: number;
  refreshKey: number;
}

const MEDAL_COLORS: Record<number, string> = {
  1: 'text-yellow-500',
  2: 'text-gray-400',
  3: 'text-amber-600',
};

function SkeletonRow() {
  return (
    <div className="flex items-center gap-3 py-2 animate-pulse">
      <div className="w-6 h-6 bg-gray-200 rounded-full" />
      <div className="flex-1 h-4 bg-gray-200 rounded" />
      <div className="w-16 h-4 bg-gray-200 rounded" />
    </div>
  );
}

function RankingCard({ title, iconColor, children }: { title: string; iconColor: string; children: React.ReactNode }) {
  return (
    <div className="flex-1 bg-white rounded-xl shadow border border-gray-200 p-5">
      <div className="flex items-center gap-2 mb-4">
        <Trophy className={`w-5 h-5 ${iconColor}`} />
        <h3 className="font-semibold text-gray-800">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function EmptyState() {
  return <p className="text-sm text-gray-500 text-center py-4">Nadie ha resuelto este ejercicio aún</p>;
}

function RankRow({ rank, fullName, stat }: { rank: number; fullName: string | null; stat: string }) {
  return (
    <li className="flex items-center gap-3">
      <span className={`text-lg font-bold w-6 text-center ${MEDAL_COLORS[rank] ?? 'text-gray-500'}`}>
        {rank}
      </span>
      <span className="flex-1 text-sm text-gray-700 truncate">{fullName}</span>
      <span className="text-sm font-semibold whitespace-nowrap">{stat}</span>
    </li>
  );
}

function FewestAttemptsCard({ entries }: { entries: RankingFewestEntry[] }) {
  return (
    <RankingCard title="TOP 5 — Menos intentos" iconColor="text-blue-500">
      {entries.length === 0 ? (
        <EmptyState />
      ) : (
        <ol className="space-y-2">
          {entries.map((e) => (
            <RankRow
              key={e.rank}
              rank={e.rank}
              fullName={e.fullName}
              stat={`${e.attempts} ${e.attempts === 1 ? 'intento' : 'intentos'}`}
            />
          ))}
        </ol>
      )}
    </RankingCard>
  );
}

function TopExecutionTimeCard({ entries }: { entries: RankingExecutionTimeEntry[] }) {
  return (
    <RankingCard title="TOP 5 — Menor tiempo de ejecución" iconColor="text-purple-500">
      {entries.length === 0 ? (
        <EmptyState />
      ) : (
        <ol className="space-y-2">
          {entries.map((e) => (
            <RankRow key={e.rank} rank={e.rank} fullName={e.fullName} stat={e.executionTime} />
          ))}
        </ol>
      )}
    </RankingCard>
  );
}

export default function ExerciseRankings({ guideNumber, exerciseNumber, refreshKey }: Props) {
  const [data, setData] = useState<ExerciseRankingsData | null>(null);
  const [loading, setLoading] = useState(true);

  // Prevents concurrent fetches (e.g. React StrictMode double-mount)
  const isExecutingRef = useRef(false);

  const cacheKey = CACHE_KEYS.exerciseRankings(guideNumber, exerciseNumber);
  const ttl = CACHE_CONFIG.exerciseRankings;

  const fetchRankings = useCallback(async () => {
    if (isExecutingRef.current) return;
    isExecutingRef.current = true;
    setLoading(true);
    try {
      const res = await apiService.getExerciseRankings(guideNumber, exerciseNumber);
      if (res.success && res.data) {
        cacheService.set(cacheKey, res.data, ttl);
        setData(res.data);
      }
    } catch {
      // silently ignore ranking errors
    } finally {
      setLoading(false);
      isExecutingRef.current = false;
    }
  }, [guideNumber, exerciseNumber, cacheKey, ttl]);

  // Initial load: serve from cache, fall back to network
  useEffect(() => {
    isExecutingRef.current = false;
    const cached = cacheService.get<ExerciseRankingsData>(cacheKey);
    if (cached) {
      setData(cached);
      setLoading(false);
    } else {
      fetchRankings();
    }
  }, [cacheKey, fetchRankings]);

  // refreshKey increments after a successful submission → bust cache and reload
  useEffect(() => {
    if (refreshKey === 0) return;
    cacheService.invalidate(cacheKey);
    fetchRankings();
  }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="flex flex-col sm:flex-row gap-6">
        {[0, 1].map((i) => (
          <div key={i} className="flex-1 bg-white rounded-xl shadow border border-gray-200 p-5">
            <div className="h-5 w-48 bg-gray-200 rounded animate-pulse mb-4" />
            {Array.from({ length: 5 }).map((_, j) => <SkeletonRow key={j} />)}
          </div>
        ))}
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="flex flex-col sm:flex-row gap-6">
      <FewestAttemptsCard entries={data.fewestAttempts} />
      <TopExecutionTimeCard entries={data.topExecutionTime} />
    </div>
  );
}
